/**
 * useToast — composable SSR-safe para mostrar toasts transitorios.
 *
 * Uso:
 *   const toast = useToast()
 *   toast.success('Operación completada')
 *   toast.error('Algo falló', 6000)   // duración custom en ms
 *   toast.info('Aviso', 0)             // 0 = no auto-dismiss
 *
 * Los toasts se renderizan en <ToastContainer /> que está montado
 * una sola vez en <GlobalOverlays /> dentro de app.vue.
 *
 * Cap FIFO de 5 toasts visibles — previene flood si algo falla en loop.
 */
import { useState } from '#imports'

export interface Toast {
  id: string
  message: string
  type: 'success' | 'error' | 'info'
  duration: number
  action?: { label: string; href?: string; onClick?: () => void }
}

const MAX_TOASTS = 5
const DEFAULT_DURATION = 4000

function safeMessage(input: unknown): string {
  if (typeof input === 'string') return input
  if (input == null) return ''
  if (input instanceof Error) return input.message || 'Error'
  if (typeof input === 'object') {
    const obj = input as Record<string, unknown>
    if (typeof obj.message === 'string') return obj.message
    if (typeof obj.error === 'string') return obj.error
    if (typeof obj.details === 'string') return obj.details
    if (typeof obj.hint === 'string') return obj.hint
    try {
      const s = JSON.stringify(input)
      // Avoid surfacing useless "{}" or oversized blobs.
      if (s === '{}' || s === '[]') return 'Error'
      return s.length > 200 ? s.slice(0, 197) + '…' : s
    } catch { /* fall through */ }
  }
  return String(input)
}

export function useToast() {
  const toasts = useState<Toast[]>('app-toasts', () => [])

  function add(rawMessage: unknown, type: Toast['type'], duration = DEFAULT_DURATION, action?: Toast['action']): string {
    const id = crypto.randomUUID()
    const message = safeMessage(rawMessage)
    toasts.value.push({ id, message, type, duration, action })

    // Cap FIFO — expulsa el más antiguo si pasamos el límite
    while (toasts.value.length > MAX_TOASTS) {
      toasts.value.shift()
    }

    // Auto-dismiss (duration=0 → persistente)
    if (duration > 0) {
      setTimeout(() => remove(id), duration)
    }
    return id
  }

  function remove(id: string): void {
    const i = toasts.value.findIndex(t => t.id === id)
    if (i >= 0) toasts.value.splice(i, 1)
  }

  return {
    toasts,
    success: (msg: unknown, duration?: number, action?: Toast['action']) => add(msg, 'success', duration, action),
    error:   (msg: unknown, duration?: number, action?: Toast['action']) => add(msg, 'error',   duration, action),
    info:    (msg: unknown, duration?: number, action?: Toast['action']) => add(msg, 'info',    duration, action),
    remove,
  }
}
